/**
 * live-avatar embeddable widget.
 *
 *   <script src="https://HOST/widget/widget.js" data-site="SITE_ID" defer></script>
 *
 * Optional: data-api="https://HOST" (defaults to the script's origin),
 *           data-position="left" (bottom-left instead of bottom-right).
 *
 * Several guides can be embedded on one page (one script tag each): only one is
 * shown at a time, with a switcher bar to swap between them. Any guide can be
 * minimized to a small face in the corner and brought back with a click.
 */

import { type Action, type Answer, Api, RateLimitError, type SiteConfig } from "./api";
import { AvatarRenderer, type Box } from "./renderer";
import { normalizeLocale, translator } from "./i18n";
import { styles } from "./styles";
import { normalizeQuestion, selectSuggestions, type SuggestionContext } from "./suggestions";

const ICONS = {
  close: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>`,
  send: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>`,
  mic: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><rect x="9" y="3" width="6" height="11" rx="3"/><path d="M5 11a7 7 0 0 0 14 0M12 18v3"/></svg>`,
  sound: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 9v6h4l5 4V5L8 9H4z"/><path d="M16.5 8.5a5 5 0 0 1 0 7"/></svg>`,
  muted: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 9v6h4l5 4V5L8 9H4z"/><path d="M17 9l4 6M21 9l-4 6"/></svg>`,
  minimize: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M7 7l10 10M17 7L7 17"/></svg>`,
};

type State = "idle" | "thinking" | "speaking" | "listening";
const STATUS_TEXT: Record<State, string> = { idle: "", thinking: "Thinking…", speaking: "Speaking", listening: "Listening…" };
const HIGHLIGHT_CLASS = "live-avatar-highlight";
const MINIMIZED_KEY = "live-avatar:minimized";
const BRAND_URL = "https://mugam.site/?ref=guide";
const MINI_FACE = 56; // px, face inside the minimized ring
const GREETING_DELAY_MS = 1200;

/** Resolves once the host page has finished loading (images, scripts, styles). */
function pageLoaded(): Promise<void> {
  if (document.readyState === "complete") return Promise.resolve();
  return new Promise((resolve) => window.addEventListener("load", () => resolve(), { once: true }));
}

/** What one guide exposes to the others on the page. */
interface Guide {
  order: number; // script-tag order: the first guide on the page is shown first
  name: string;
  position: "left" | "right";
  avatarUrl: string;
  size: [number, number];
  face: Box;
  layout(): void;
  stop(): void;
  greet(): void;
}

interface Registry {
  guides: Guide[];
  active: Guide | null;
  minimized: boolean;
}

// Each <script> tag runs its own copy of this bundle, so shared state lives on window.
function registry(): Registry {
  const w = window as unknown as { __liveAvatar?: Registry };
  if (!w.__liveAvatar) {
    let minimized = false;
    try {
      minimized = localStorage.getItem(MINIMIZED_KEY) === "1";
    } catch {}
    w.__liveAvatar = { guides: [], active: null, minimized };
  }
  return w.__liveAvatar;
}

function nextOrder(): number {
  const w = window as unknown as { __liveAvatarOrder?: number };
  w.__liveAvatarOrder = (w.__liveAvatarOrder ?? 0) + 1;
  return w.__liveAvatarOrder - 1;
}

function relayout(): void {
  for (const g of registry().guides) g.layout();
}

function setMinimized(minimized: boolean): void {
  const reg = registry();
  reg.minimized = minimized;
  try {
    localStorage.setItem(MINIMIZED_KEY, minimized ? "1" : "0");
  } catch {}
  if (minimized) reg.active?.stop();
  relayout();
}

function activate(guide: Guide): void {
  const reg = registry();
  if (reg.active && reg.active !== guide) reg.active.stop();
  reg.active = guide;
  reg.minimized = false;
  relayout();
  guide.greet();
}

/** CSS that fills a circle of `size` px with the guide's face, cropped from the avatar image. */
function faceCss(g: Guide, size: number): string {
  const [x1, y1, x2, y2] = g.face;
  const scale = size / (Math.max(x2 - x1, y2 - y1) * 1.18);
  const cx = (x1 + x2) / 2;
  const cy = (y1 + y2) / 2 - (y2 - y1) * 0.06;
  return [
    `background-image:url("${g.avatarUrl}")`,
    `background-size:${g.size[0] * scale}px ${g.size[1] * scale}px`,
    `background-position:${size / 2 - cx * scale}px ${size / 2 - cy * scale}px`,
  ].join(";");
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Record<string, string> = {}, html = ""): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  if (html) node.innerHTML = html;
  return node;
}

/** Highlight style lives in the host page, since highlighted elements are outside the shadow root. */
function installHighlightStyle(accent: string): void {
  if (document.getElementById("live-avatar-highlight-style")) return;
  const style = el("style", { id: "live-avatar-highlight-style" });
  style.textContent = `
    .${HIGHLIGHT_CLASS} { outline: 3px solid ${accent} !important; outline-offset: 6px; border-radius: 6px; animation: live-avatar-hl 1.4s ease-in-out 2; }
    @keyframes live-avatar-hl { 50% { outline-color: transparent; } }
    @media (prefers-reduced-motion: reduce) { .${HIGHLIGHT_CLASS} { animation: none; } }`;
  document.head.append(style);
}

class Widget {
  private readonly t: ReturnType<typeof translator>;
  private readonly shadow: ShadowRoot;
  private readonly ui;
  private readonly video: HTMLVideoElement;
  private renderer?: AvatarRenderer;
  private abort?: AbortController;
  private greeted = false;
  private greetingFetch?: Promise<void>;
  private greetingSrc?: string; // blob: URL of the downloaded greeting clip
  private state: State = "idle";
  private muted = false;
  private guide?: Guide;
  private readonly askedQuestions = new Set<string>();
  private hasAsked = false;
  private activeQuestion?: string;
  private suggestionContext: SuggestionContext = {};

  constructor(
    host: HTMLElement,
    private readonly api: Api,
    private readonly site: SiteConfig,
    private readonly position: "left" | "right",
    private readonly order: number,
    uiLanguage: string,
    private readonly contentPath: string,
  ) {
    this.t = translator(uiLanguage);
    host.lang = uiLanguage;
    this.shadow = host.attachShadow({ mode: "open" });
    const name = site.assistant.name;
    const style = el("style");
    style.textContent = styles;

    // Hidden until loaded and laid out, so guides that end up inactive never flash.
    const root = el("div", { class: position === "left" ? "root left" : "root", hidden: "" });
    const panel = el("section", { class: "panel", "aria-label": this.t("Chat with {name}", {name}), hidden: "" });
    const close = el("button", { class: "icon-btn", type: "button", "aria-label": this.t("Close chat") }, ICONS.close);
    const showSuggestions = el("button", { class: "show-suggestions", type: "button", "aria-label": this.t("Show suggested questions"), hidden: "" });
    showSuggestions.textContent = this.t("Questions");
    const headerActions = el("div", { class: "header-actions" });
    headerActions.append(showSuggestions, close);
    const header = el("header");
    header.append(el("div", {}, `<strong></strong><span></span>`), headerActions);
    header.querySelector("strong")!.textContent = name;
    header.querySelector("span")!.textContent = this.t("AI guide \u00b7 {business}", {business: site.business_name});
    const log = el("div", { class: "log", role: "log", "aria-live": "polite" });
    const suggestions = el("section", { class: "suggestions", "aria-label": this.t("Suggested questions") });
    const suggestionLabel = el("div", { class: "suggestion-label" });
    const hideSuggestions = el("button", { class: "icon-btn suggestion-close", type: "button", "aria-label": this.t("Hide suggested questions") }, ICONS.close);
    const suggestionHeader = el("div", { class: "suggestion-header" });
    suggestionHeader.append(suggestionLabel, hideSuggestions);
    const chips = el("div", { class: "chips" });
    const suggestionEmpty = el("div", { class: "suggestion-empty", hidden: "" });
    suggestionEmpty.textContent = this.t("You've explored these questions. Ask anything below.");
    suggestions.append(suggestionHeader, chips, suggestionEmpty);
    const form = el("form");
    const mic = el("button", { class: "icon-btn mic", type: "button", "aria-label": this.t("Speak your question"), "aria-pressed": "false" }, ICONS.mic);
    const input = el("input", { type: "text", placeholder: this.t("Ask {name} anything\u2026", {name}), "aria-label": this.t("Your question"), autocomplete: "off", maxlength: "500" });
    const send = el("button", { class: "icon-btn send", type: "submit", "aria-label": this.t("Send") }, ICONS.send);
    form.append(mic, input, send);
    panel.append(header, log, suggestions, form);

    const stage = el("div", { class: "stage" });
    const avatar = el("button", { class: "avatar", type: "button", "aria-label": this.t("Chat with {name}", {name}) });
    const canvas = el("canvas");
    avatar.append(canvas);
    const status = el("div", { class: "status", hidden: "" });
    const controls = el("div", { class: "controls" });
    const minimize = el("button", { class: "ctl", type: "button", "aria-label": this.t("Hide {name}", {name}) }, ICONS.minimize);
    const mute = el("button", { class: "ctl mute", type: "button", "aria-label": this.t("Mute"), "aria-pressed": "false" }, ICONS.sound);
    controls.append(minimize, mute);
    const bubble = el("div", { class: "bubble", hidden: "" });
    const switcher = el("div", { class: "switcher", role: "group", "aria-label": this.t("Choose a guide"), hidden: "" });
    stage.append(avatar, status, controls, bubble, switcher);
    const mini = el("button", { class: "mini", type: "button", "aria-label": this.t("Show {name}", {name}), hidden: "" });
    const miniFace = el("span", { class: "mini-face", "aria-hidden": "true" });
    mini.append(miniFace);
    if (site.branding !== false) {
      // "MUGAM" along the top of a ring around the minimized face.
      mini.classList.add("branded");
      mini.insertAdjacentHTML(
        "afterbegin",
        `<svg class="mini-ring" viewBox="0 0 76 76" aria-hidden="true"><defs><path id="mugam-arc" d="M8.5 38a29.5 29.5 0 0 1 59 0"/></defs>` +
          `<circle cx="38" cy="38" r="38" style="fill:var(--la-accent)"/>` +
          `<text><textPath href="#mugam-arc" startOffset="50%" text-anchor="middle">MUGAM</textPath></text></svg>`,
      );
      const badge = el("a", { class: "badge", href: BRAND_URL, target: "_blank", rel: "noopener", "aria-label": this.t("Made with Mugam.site") });
      badge.innerHTML = `mugam<img src="${api.url("/static/face-dot.svg")}" alt="">site`;
      stage.append(badge);
    }

    // One reusable element: once a click has started it, browsers allow later clips to play with sound.
    this.video = el("video", { playsinline: "", preload: "auto", crossorigin: "anonymous" });
    this.video.hidden = true;

    root.append(panel, stage, mini, this.video);
    this.shadow.append(style, root);
    if (site.accent) host.style.setProperty("--la-accent", site.accent);

    this.ui = { root, panel, stage, log, suggestions, suggestionLabel, suggestionEmpty, chips, form, input, mic, avatar, canvas, status, mute, bubble, switcher, mini, miniFace };
    this.refreshSuggestions();

    avatar.addEventListener("click", () => this.togglePanel());
    bubble.addEventListener("click", () => this.togglePanel(true));
    close.addEventListener("click", () => this.togglePanel(false));
    hideSuggestions.addEventListener("click", () => {
      suggestions.hidden = true;
      showSuggestions.hidden = false;
      input.focus();
    });
    showSuggestions.addEventListener("click", () => {
      suggestions.hidden = false;
      showSuggestions.hidden = true;
      hideSuggestions.focus();
    });
    mute.addEventListener("click", () => this.toggleMute());
    minimize.addEventListener("click", () => setMinimized(true));
    mini.addEventListener("click", () => setMinimized(false));
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      const q = input.value.trim();
      if (q) this.ask(q);
    });
    this.video.addEventListener("ended", () => this.endClip());
    this.video.addEventListener("error", () => this.endClip());
    this.setupMic();
    installHighlightStyle(site.accent || "#4f46e5");
  }

  async load(): Promise<void> {
    const { img, parts, sheet } = await this.api.avatar(this.site);
    this.renderer = new AvatarRenderer(this.ui.canvas, img, parts, sheet);
    this.renderer.start();
    window.addEventListener("resize", () => this.renderer?.resize());

    const guide: Guide = (this.guide = {
      order: this.order,
      name: this.site.assistant.name,
      position: this.position,
      avatarUrl: img.src,
      size: [parts.width, parts.height],
      face: parts.boxes.face,
      layout: () => this.layout(),
      stop: () => this.stop(),
      greet: () => this.greet(),
    });
    const reg = registry();
    reg.guides.push(guide);
    reg.guides.sort((a, b) => a.order - b.order);
    // The first guide on the page leads; a later-loading earlier guide takes over.
    const lead = !reg.active || guide.order < reg.active.order;
    if (lead) reg.active?.stop();
    if (lead) reg.active = guide;
    relayout();
    if (lead) setTimeout(() => this.greet(), GREETING_DELAY_MS);
  }

  /** Show, hide or minimize this guide based on the page-wide registry. */
  private layout(): void {
    const reg = registry();
    const { root, stage, panel, mini, switcher } = this.ui;
    const active = reg.active === this.guide;
    root.hidden = !active;
    if (!active) return;
    const position = reg.guides.length > 1 ? reg.guides[0].position : this.position;
    root.classList.toggle("left", position === "left");
    stage.hidden = reg.minimized;
    mini.hidden = !reg.minimized;
    if (reg.minimized) {
      panel.hidden = true;
      this.ui.bubble.hidden = true;
      this.ui.miniFace.setAttribute("style", faceCss(this.guide!, MINI_FACE));
    }
    this.renderer?.resize();

    switcher.hidden = reg.guides.length < 2;
    switcher.replaceChildren(
      ...reg.guides.map((g) => {
        const b = el("button", { type: "button", class: "switch", "aria-pressed": String(g === this.guide), title: g.name });
        b.append(el("span", { class: "face", style: faceCss(g, 30), "aria-hidden": "true" }));
        const label = el("span", { class: "label" });
        label.textContent = g.name;
        b.append(label);
        b.addEventListener("click", () => g !== this.guide && activate(g));
        return b;
      }),
    );
  }

  private stop(): void {
    this.abort?.abort();
    this.activeQuestion = undefined;
    this.refreshSuggestions();
    this.video.pause();
    this.endClip();
    this.ui.panel.hidden = true;
    this.ui.bubble.hidden = true;
  }

  private greet(): void {
    const reg = registry();
    if (reg.active !== this.guide) return;
    this.prefetchGreeting();
    if (reg.minimized || !this.site.greeting) return;
    this.showBubble(this.site.greeting.text, this.t("Tap to chat"));
  }

  /**
   * Download the greeting clip once this guide's avatar is showing and the host page has
   * finished loading, so it never competes with either, and the first open plays instantly.
   */
  private prefetchGreeting(): void {
    const clip = this.site.greeting?.clip;
    if (!clip || this.greetingFetch) return;
    this.greetingFetch = pageLoaded()
      .then(() => fetch(this.api.url(clip)))
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        this.greetingSrc = URL.createObjectURL(blob);
      })
      .catch((err) => console.warn("[live-avatar] greeting prefetch failed", err));
  }

  private togglePanel(open = this.ui.panel.hidden): void {
    this.ui.panel.hidden = !open;
    this.ui.bubble.hidden = true;
    if (!open) return;
    if (!this.greeted && this.site.greeting) {
      // First open is a user gesture, so the browser lets the greeting play with sound.
      this.greeted = true;
      this.addMessage("assistant", this.site.greeting.text);
      this.play(this.greetingSrc ?? this.site.greeting.clip);
    }
    this.ui.input.focus();
  }

  private toggleMute(): void {
    this.muted = !this.muted;
    this.video.muted = this.muted;
    this.ui.mute.setAttribute("aria-pressed", String(this.muted));
    this.ui.mute.setAttribute("aria-label", this.muted ? this.t("Unmute") : this.t("Mute"));
    this.ui.mute.innerHTML = this.muted ? ICONS.muted : ICONS.sound;
  }

  private setState(state: State): void {
    this.state = state;
    this.ui.status.hidden = state === "idle";
    this.ui.status.dataset.state = state;
    this.ui.status.textContent = this.t(STATUS_TEXT[state]);
  }

  private showBubble(text: string, hint = ""): void {
    if (!this.ui.panel.hidden) return;
    this.ui.bubble.hidden = false;
    this.ui.bubble.textContent = text;
    if (hint) this.ui.bubble.append(el("small", {}, hint));
  }

  private addMessage(role: "user" | "assistant", text: string): HTMLElement {
    const msg = el("div", { class: `msg ${role}` });
    msg.textContent = text;
    this.ui.log.append(msg);
    this.ui.log.scrollTop = this.ui.log.scrollHeight;
    return msg;
  }

  private play(clip: string | null | undefined): void {
    if (!clip) return;
    this.video.src = this.api.url(clip);
    this.video.muted = this.muted;
    this.video.currentTime = 0;
    this.video.play().then(
      () => {
        this.renderer?.showVideo(this.video);
        this.setState("speaking");
      },
      (err) => {
        console.warn("[live-avatar] clip blocked", err);
        this.endClip();
      },
    );
  }

  private endClip(): void {
    this.renderer?.showStill();
    if (this.state === "speaking") this.setState("idle");
  }

  private refreshSuggestions(): void {
    const options = this.site.suggestion_topics ?? this.site.suggestions.map((question) => ({ question }));
    const visible = selectSuggestions(options, this.askedQuestions, this.activeQuestion,
      this.suggestionContext, this.hasAsked ? 3 : 6);
    this.ui.suggestions.dataset.started = String(this.hasAsked);
    this.ui.suggestionLabel.textContent = this.hasAsked ? this.t("You could also ask") : this.t("Try a question");
    this.ui.suggestionEmpty.hidden = visible.length > 0;
    this.ui.chips.replaceChildren(...visible.map(({ question }) => {
      const chip = el("button", { class: "chip", type: "button" });
      chip.textContent = question;
      chip.addEventListener("click", () => this.ask(question));
      return chip;
    }));
  }

  async ask(question: string): Promise<void> {
    if (this.ui.panel.hidden) this.togglePanel(true);
    this.abort?.abort();
    this.video.pause();
    this.endClip();
    this.ui.input.value = "";
    this.hasAsked = true;
    this.activeQuestion = question;
    this.suggestionContext = { question };
    this.refreshSuggestions();
    this.ui.input.focus();

    this.addMessage("user", question);
    const reply = this.addMessage("assistant", "");
    reply.replaceChildren(el("span", {class: "typing", "aria-label": this.t("Typing")}, "<i></i><i></i><i></i>"));
    this.setState("thinking");

    const abort = (this.abort = new AbortController());
    let answer: Answer;
    try {
      answer = await this.api.ask(this.site.id, question, abort.signal);
    } catch (err) {
      if (abort.signal.aborted) { reply.remove(); return; }
      reply.classList.add("error");
      reply.textContent = err instanceof RateLimitError
        ? this.t("Please wait {seconds} seconds before asking another question.", {seconds: err.retryAfter})
        : this.t("Sorry, I couldn't reach the server. Please try again.");
      this.setState("idle");
      this.activeQuestion = undefined;
      this.refreshSuggestions();
      console.warn("[live-avatar]", err);
      return;
    }
    if (abort.signal.aborted) { reply.remove(); return; }
    this.askedQuestions.add(normalizeQuestion(question));
    if (answer.answered_question) this.askedQuestions.add(normalizeQuestion(answer.answered_question));
    this.activeQuestion = undefined;
    this.suggestionContext = { question, answer: answer.text, page_url: answer.page_url, anchor: answer.anchor };
    this.refreshSuggestions();
    reply.textContent = answer.text;
    this.ui.log.scrollTop = this.ui.log.scrollHeight;
    this.setState("idle");
    this.play(answer.clip);
    this.showOnPage(answer, reply);
    this.showAction(answer.action, question);
  }

  /** The owner's follow-up for this answer: a link button, or a short form sent to them. */
  private showAction(action: Action | null | undefined, question: string): void {
    if (!action) return;
    const box = el("div", { class: "action" });
    if (action.type === "link") {
      if (!/^(https?|mailto|tel):/i.test(action.url)) return; // the server only stores these
      const sameSite = /^https?:/.test(action.url) && new URL(action.url).host === location.host;
      const link = el("a", { class: "action-link", href: action.url, ...(sameSite ? {} : { target: "_blank", rel: "noopener" }) });
      link.textContent = `${action.label} →`;
      box.append(link);
    } else {
      box.append(this.leadForm(action, question));
    }
    this.ui.log.append(box);
    this.ui.log.scrollTop = this.ui.log.scrollHeight;
  }

  private leadForm(action: Extract<Action, { type: "form" }>, question: string): HTMLFormElement {
    const form = el("form", { class: "lead", novalidate: "" }) as HTMLFormElement;
    const title = el("div", { class: "lead-title" });
    title.textContent = action.title;
    form.append(title);
    const inputs = new Map<string, HTMLInputElement | HTMLTextAreaElement>();
    const uid = `${this.site.id}-${action.qa_id}-${this.ui.log.childElementCount}`;
    for (const field of action.fields) {
      const id = `lead-${uid}-${field.id}`;
      const label = el("label", { for: id });
      label.textContent = field.label + (field.required ? " *" : "");
      const input = (field.kind === "textarea"
        ? el("textarea", { id, rows: "3", maxlength: "2000" })
        : el("input", { id, type: field.kind, maxlength: "200", ...(field.kind === "email" ? { autocomplete: "email" } : field.kind === "tel" ? { autocomplete: "tel" } : {}) })
      ) as HTMLInputElement | HTMLTextAreaElement;
      if (field.required) input.required = true;
      inputs.set(field.id, input);
      form.append(label, input);
    }
    // Hidden from people (and from assistive tech); bots that fill every field get ignored.
    const trap = el("input", { type: "text", name: "website", tabindex: "-1", autocomplete: "off", class: "trap", "aria-hidden": "true" }) as HTMLInputElement;
    const status = el("div", { class: "lead-status", role: "status" });
    const send = el("button", { type: "submit", class: "lead-send" }) as HTMLButtonElement;
    send.textContent = action.button;
    const note = el("div", { class: "lead-note" });
    note.textContent = this.t("Your details go to {business}.", {business: this.site.business_name || this.t("the site owner")});
    form.append(trap, send, status, note);

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      const missing = action.fields.find((f) => f.required && !inputs.get(f.id)!.value.trim());
      if (missing) {
        status.textContent = this.t("Please fill in {field}.", {field: missing.label});
        inputs.get(missing.id)!.focus();
        return;
      }
      send.disabled = true;
      status.textContent = this.t("Sending…");
      try {
        const message = await this.api.submitLead(this.site.id, {
          qa_id: action.qa_id,
          values: Object.fromEntries([...inputs].map(([id, input]) => [id, input.value.trim()])),
          question,
          page_url: location.href,
          website: trap.value,
        });
        const done = el("div", { class: "lead-done" });
        done.textContent = message;
        form.replaceWith(done);
      } catch (err) {
        send.disabled = false;
        status.textContent = err instanceof Error ? err.message : this.t("Couldn't send. Please try again.");
      }
    });
    return form;
  }

  /** Scroll to and highlight the answer's section, or offer a link to the page that has it. */
  private showOnPage(answer: Answer, reply: HTMLElement): void {
    const target = answer.anchor ? document.getElementById(answer.anchor) : null;
    const pageUrl = answer.page_url ? new URL(answer.page_url) : null;
    const samePage = !pageUrl || (pageUrl.host === location.host && pageUrl.pathname === this.contentPath);

    if (target && samePage) {
      target.scrollIntoView({ behavior: "smooth", block: "center" });
      target.classList.add(HIGHLIGHT_CLASS);
      setTimeout(() => target.classList.remove(HIGHLIGHT_CLASS), 3200);
      return;
    }
    if (pageUrl && !samePage) {
      const href = pageUrl.href.split("#")[0] + (answer.anchor ? `#${answer.anchor}` : "");
      const sameSite = pageUrl.host === location.host;
      const link = el("a", { class: "go", href, ...(sameSite ? {} : { target: "_blank", rel: "noopener" }) });
      link.textContent = this.t("Show me on the page →");
      reply.append(el("br"), link);
    }
  }

  private setupMic(): void {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const { mic, input } = this.ui;
    if (!SR) {
      mic.hidden = true;
      return;
    }
    let rec: any = null;
    mic.addEventListener("click", () => {
      if (rec) return rec.stop();
      this.video.pause();
      this.endClip();
      rec = new SR();
      rec.lang = document.documentElement.lang || "en-US";
      rec.interimResults = true;
      rec.onresult = (e: any) => {
        const res = e.results[e.results.length - 1];
        input.value = res[0].transcript;
        if (res.isFinal) this.ask(input.value.trim());
      };
      rec.onend = rec.onerror = () => {
        rec = null;
        mic.setAttribute("aria-pressed", "false");
        if (this.state === "listening") this.setState("idle");
      };
      rec.start();
      mic.setAttribute("aria-pressed", "true");
      this.setState("listening");
    });
  }
}

async function boot(tag: HTMLScriptElement | null, order: number): Promise<void> {
  if (!tag?.dataset.site) return console.warn("[live-avatar] missing data-site on the script tag");
  const uiLanguage = normalizeLocale(tag.dataset.uiLanguage || document.documentElement.lang);
  const api = new Api(tag.dataset.api ?? new URL(tag.src).origin, translator(uiLanguage));

  const host = document.createElement("live-avatar");
  try {
    const site = await api.site(tag.dataset.site);
    const widget = new Widget(host, api, site, tag.dataset.position === "left" ? "left" : "right", order, uiLanguage, tag.dataset.contentPath || location.pathname);
    document.body.append(host);
    await widget.load();
  } catch (err) {
    host.remove();
    console.warn("[live-avatar] failed to start", err);
  }
}

// document.currentScript is only set while the script first executes, so capture it now.
const tag =
  (document.currentScript as HTMLScriptElement | null) ?? document.querySelector<HTMLScriptElement>("script[data-site]");
const order = nextOrder();
if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => boot(tag, order), { once: true });
else void boot(tag, order);
